import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ListOutlinedIcon from '@mui/icons-material/ListOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Stack } from '@mui/system';
import { ImportButton } from 'components/ExportImport/NxtImport/components/ImportButton';
import { Fragment, memo } from 'react';
import { bloxValues } from 'themes/constants';
import { ISearchState, IViewState, PlusNavObj } from '../types';
import AppSearchResults from './AppSearchResults';
import PinnedApps from './PinnedApps';
import RecommendedApps from './RecommendedApps';
import StoryTemplatesSection from './StoryTemplatesSection';
import { menuIds } from 'providers/contexts/analytics/AnalyticsContext';

interface MainViewContentProps {
  allApps: PlusNavObj[];
  currentAppStr: string;
  editMode: boolean;
  handleBackClick: () => void;
  handleNxtImportView: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleClose: () => void;
  handleItemClick: (event: React.SyntheticEvent<HTMLElement>, item: PlusNavObj) => void;
  handlePinToggle: (item: PlusNavObj) => void;
  handleReorderApps: (oldIndex: number, newIndex: number) => void;
  handleShowAllAppsClick: () => void;
  isOpen: boolean;
  pinnedApps: string[];
  pinnedTemplateItems: PlusNavObj[];
  recommendedApps: PlusNavObj[];
  searchResults: PlusNavObj[];
  searchState: ISearchState;
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchState: React.Dispatch<React.SetStateAction<ISearchState>>;
  setViewState: React.Dispatch<React.SetStateAction<IViewState>>;
  showAllApps: boolean;
  searchFieldRef: React.RefObject<HTMLInputElement>;
  showTemplates: boolean;
}

const MainViewContent = memo(
  ({
    allApps,
    editMode,
    handleBackClick,
    handleNxtImportView,
    handleClose,
    handleItemClick,
    handlePinToggle,
    handleReorderApps,
    handleShowAllAppsClick,
    isOpen,
    pinnedApps,
    pinnedTemplateItems,
    recommendedApps,
    searchResults,
    searchState,
    setEditMode,
    setSearchState,
    showAllApps,
    searchFieldRef,
    showTemplates,
  }: MainViewContentProps) => {
    const theme = useTheme();
    const plusButtonMainGridId = menuIds.global['Grid Header Create Asset'].id;

    return (
      <Box
        id={plusButtonMainGridId}
        data-testid={plusButtonMainGridId}
        data-state={isOpen ? 'open' : 'closed'}
        sx={{
          display: 'grid',
          gridTemplateAreas: `
            "header"
            "content"
          `,
          gridTemplateRows: 'auto 1fr',
          height: '100%',
          width: '100%',
        }}
      >
        <Stack
          id="plus-button-main-header"
          data-testid="plus-button-main-header"
          direction="row"
          sx={{
            alignItems: 'center',
            gridArea: 'header',
            borderBottom: `1px solid ${theme.palette.divider}`,
            gap: 1,
            p: 1,
          }}
        >
          {showAllApps ? (
            <Tooltip
              title="Back"
              disableInteractive
            >
              <IconButton
                onClick={handleBackClick}
                data-testid="plus-button-main-back"
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip
              title="Show All"
              data-testid="plus-button-main-show-all"
              disableInteractive
            >
              <IconButton onClick={handleShowAllAppsClick}>
                <ListOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            title={editMode ? 'Hide Pins' : 'Edit Pins'}
            disableInteractive
          >
            <IconButton
              onClick={() => setEditMode(!editMode)}
              size="small"
              data-testid="plus-button-main-edit-pins"
              sx={{
                transform: 'rotate(45deg)',
              }}
            >
              {editMode ? <PushPinRoundedIcon /> : <PushPinOutlinedIcon />}
            </IconButton>
          </Tooltip>
          <TextField
            id="plus-button-search-field"
            data-testid="plus-button-search-field"
            // When templates are available this one bar also searches them, so
            // advertise that; otherwise it's just the create-new app search.
            placeholder={
              showTemplates ? 'Create new or search templates...' : 'Create new...'
            }
            value={searchState.query}
            onChange={(e) => setSearchState({ ...searchState, query: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth={true}
            inputRef={searchFieldRef}
            sx={{
              p: 0,
              m: 0,
              fontSize: bloxValues.smallFontSizeRem,
              '& .MuiOutlinedInput-root': {
                fontSize: bloxValues.smallFontSizeRem,
                '& fieldset': {
                  fontSize: bloxValues.smallFontSizeRem,
                },
              },
            }}
            slotProps={{
              input: {
                id: 'plus-button-search-input',
                // @ts-ignore - data-testid isn't in the types but it should still be applied to the input
                'data-testid': 'plus-button-search-input',
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
          <ImportButton
            id="plus-nxt-import-button"
            testId="plus-nxt-import-button"
            onClickFunction={handleNxtImportView}
            tooltip="Import"
          />
          <Tooltip
            title="Close"
            disableInteractive
            data-testid="plus-button-main-close-button"
          >
            <IconButton onClick={handleClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Box
          id="plus-button-main-content"
          data-testid="plus-button-main-content"
          sx={{
            p: 1,
            gridArea: 'content',
            overflow: 'auto',
            // Without this the 1fr grid row's default `min-height: auto` lets the
            // content grow past the Paper instead of scrolling, clipping the last
            // row (e.g. the "Search All Templates" link).
            minHeight: 0,
          }}
        >
          <Stack
            direction="column"
            sx={{
              gap: 1,
            }}
          >
            {showAllApps ? (
              <AppSearchResults
                searchResults={searchResults}
                handleItemClick={handleItemClick}
                handlePinToggle={handlePinToggle}
                pinnedApps={pinnedApps}
                editMode={editMode}
              />
            ) : (
              <Fragment>
                <PinnedApps
                  allApps={[...allApps, ...pinnedTemplateItems]}
                  pinnedApps={pinnedApps}
                  handleItemClick={handleItemClick}
                  handlePinToggle={handlePinToggle}
                  editMode={editMode}
                  handleReorderApps={handleReorderApps}
                />
                <RecommendedApps
                  recommendedApps={recommendedApps}
                  handleItemClick={handleItemClick}
                  editMode={editMode}
                  handlePinToggle={handlePinToggle}
                  pinnedApps={pinnedApps}
                />
              </Fragment>
            )}
            {showTemplates ? (
              <StoryTemplatesSection
                query={searchState.query}
                isOpen={isOpen}
                editMode={editMode}
                pinnedApps={pinnedApps}
                handleItemClick={handleItemClick}
                handlePinToggle={handlePinToggle}
              />
            ) : null}
          </Stack>
        </Box>
      </Box>
    );
  },
);

MainViewContent.displayName = 'MainViewContent';
export default MainViewContent;
